import { useState } from 'react';
import { addToast } from '@heroui/react';
import {
  TeacherRequestReason,
  useUpdateTeacherRequestMutation,
  type ITeacherRequestDetail,
} from '@/entities/TeacherRequests';
import { AppButton } from '@/shared/ui/AppButton';
import { DateSelectField, SelectField, TextareaField } from '@/shared/ui/Fields';
import { TEACHER_REQUEST_REASON_OPTIONS } from '../../components/TeacherRequests/teacherRequestUi';
import { RequestAttachmentList } from './RequestAttachmentList';
import { MAX_REQUEST_FILES, RequestFilesField } from './RequestFilesField';

interface TeacherRequestEditFormProps {
  request: ITeacherRequestDetail;
  onDone: () => void;
}

const getErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message);
  }

  return 'Failed to save the request';
};

// Дата приходит с сервера полным ISO, а поле даты работает с YYYY-MM-DD.
const toDateInput = (value: string | null) => value?.slice(0, 10) ?? '';

export const TeacherRequestEditForm = ({ request, onDone }: TeacherRequestEditFormProps) => {
  const [reason, setReason] = useState<TeacherRequestReason>(request.reason);
  const [comment, setComment] = useState(request.staffComment);
  const [startDate, setStartDate] = useState(toDateInput(request.startDate));
  const [endDate, setEndDate] = useState(toDateInput(request.endDate));
  const [files, setFiles] = useState<File[]>([]);
  const [removedKeys, setRemovedKeys] = useState<string[]>([]);

  const [updateTeacherRequest, { isLoading }] = useUpdateTeacherRequestMutation();

  const keptAttachments = request.staffAttachments.filter(
    (attachment) => !removedKeys.includes(attachment.key),
  );

  const canSave = Boolean(reason && comment.trim());

  const handleSave = async () => {
    if (!canSave) return;

    try {
      await updateTeacherRequest({
        requestId: request.id,
        reason,
        staffComment: comment.trim(),
        startDate: startDate ? new Date(startDate).toISOString() : null,
        endDate: endDate ? new Date(endDate).toISOString() : null,
        staffAttachments: files.length ? files : undefined,
        removeAttachmentKeys: removedKeys.length ? removedKeys : undefined,
      }).unwrap();

      addToast({ title: 'Request updated', color: 'success' });
      onDone();
    } catch (error) {
      addToast({ title: getErrorMessage(error), color: 'danger' });
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <SelectField
        name="editTeacherReason"
        label="Reason"
        placeholder="Select reason"
        value={reason}
        options={TEACHER_REQUEST_REASON_OPTIONS}
        onChange={(value) => setReason(value as TeacherRequestReason)}
      />

      <div className="grid grid-cols-2 gap-5">
        <DateSelectField
          name="editTeacherStartDate"
          label="Period start (optional)"
          value={startDate}
          dateFormat="dd/MM/yyyy"
          onChange={(_, value) => setStartDate(value)}
        />
        <DateSelectField
          name="editTeacherEndDate"
          label="Period end (optional)"
          value={endDate}
          minDate={startDate ? new Date(startDate) : undefined}
          dateFormat="dd/MM/yyyy"
          onChange={(_, value) => setEndDate(value)}
        />
      </div>

      <TextareaField
        name="editTeacherComment"
        label="Comment"
        placeholder="Enter your comment"
        rows={5}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
      />

      <RequestAttachmentList
        attachments={keptAttachments}
        onRemove={(key) => setRemovedKeys((current) => [...current, key])}
      />

      <RequestFilesField
        files={files}
        onChange={setFiles}
        maxFiles={Math.max(MAX_REQUEST_FILES - keptAttachments.length, 0)}
      />

      <div className="mt-4 grid grid-cols-2 gap-6">
        <AppButton color="white" height={56} onPress={onDone}>
          Cancel
        </AppButton>
        <AppButton
          color="red"
          height={56}
          disabled={!canSave}
          isLoading={isLoading}
          onPress={handleSave}
        >
          Save
        </AppButton>
      </div>
    </div>
  );
};
